import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { downloadBlob } from '../../../utils/download';
import { stringToSlug } from '../../../utils/slug';
import type { Theme, ThemeCreateRequest, ThemeRenameRequest } from '../types/theme';
import { useThemesApi } from './use-themes-api';

const THEMES_QUERY_KEY = ['themes'] as const;

/**
 * Fetch themes, sorted with published themes first, then most-recently-updated.
 */
export function useThemes() {
  const { themesApi } = useThemesApi();
  return useQuery({
    queryKey: THEMES_QUERY_KEY,
    queryFn: () => themesApi.list(),
    select: (themes: Theme[]) =>
      [...themes].sort((a, b) => {
        if (a.is_published !== b.is_published) {
          return a.is_published ? -1 : 1;
        }
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }),
  });
}

export function useCreateTheme() {
  const { themesApi } = useThemesApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ThemeCreateRequest) => themesApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THEMES_QUERY_KEY });
    },
  });
}

export function useRenameTheme() {
  const { themesApi } = useThemesApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ThemeRenameRequest }) =>
      themesApi.rename(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THEMES_QUERY_KEY });
    },
  });
}

export function usePublishTheme() {
  const { themesApi } = useThemesApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => themesApi.publish(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THEMES_QUERY_KEY });
    },
  });
}

export function useDeleteTheme() {
  const { themesApi } = useThemesApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => themesApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THEMES_QUERY_KEY });
    },
  });
}

export function useUploadTheme() {
  const { themesApi } = useThemesApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, base64 }: { id: string; base64: string }) => themesApi.uploadZip(id, base64),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THEMES_QUERY_KEY });
    },
  });
}

export function useDownloadTheme() {
  const { themesApi } = useThemesApi();
  return useMutation({
    mutationFn: async (theme: Theme) => {
      const buffer = await themesApi.downloadZip(theme.id);
      const blob = new Blob([buffer], { type: 'application/zip' });
      const filename = `${stringToSlug(theme.name)}-${new Date().toJSON().replace('.', '-')}.zip`;
      downloadBlob(blob, filename);
    },
  });
}
